<template>
  <div class="sli-show-page">
    <header class="sli-show-bar">
      <div class="sli-show-bar__text">
        <h1>عرض الحصر</h1>
        <p>
          {{ display(form.subject_name) }}
          <span v-if="form.shahr_number"> — شهر {{ form.shahr_number }}</span>
          <span v-if="form.land_code"> — كود {{ form.land_code }}</span>
        </p>
      </div>
      <nav class="sli-show-bar__nav">
        <router-link
          class="sli-show-btn sli-show-btn--primary"
          :to="editTo"
        >
          تعديل
        </router-link>
        <button
          v-if="inventory"
          type="button"
          class="sli-show-btn sli-show-btn--ghost"
          @click="openMedia"
        >
          المستندات والخريطة
          <span
            v-if="mediaCount"
            class="sli-show-btn__count"
          >{{ mediaCount }}</span>
        </button>
        <button
          v-if="canLink"
          type="button"
          class="sli-show-btn sli-show-btn--ghost"
          @click="linkModal = true"
        >
          ربط الاستيلاء
        </button>
        <router-link
          class="sli-show-btn sli-show-btn--ghost"
          :to="{ name: 'seizure-land-inventories-list', query: listQuery }"
        >
          رجوع للقائمة
        </router-link>
      </nav>
    </header>

    <div
      v-if="loading"
      class="sli-show-loading"
    >
      <b-spinner variant="primary" />
    </div>

    <template v-else-if="inventory">
      <section class="sli-show-kpis">
        <span class="sli-kpi">{{ parcels.length }} قطع</span>
        <span class="sli-kpi">{{ encroachmentsCount(form) }} تعديات</span>
        <span class="sli-kpi">{{ linkedParcelsCount(form) }} مربوط</span>
        <span
          v-if="form.header_type"
          class="sli-kpi sli-kpi--muted"
        >{{ headerTypeLabel }}</span>
      </section>

      <article class="sli-show-card sli-show-card--header">
        <header class="sli-show-card__head">
          بيانات الشهر والخاضع
        </header>
        <dl class="sli-facts">
          <div
            v-for="row in headerFacts"
            :key="row.label"
            class="sli-fact"
          >
            <dt>{{ row.label }}</dt>
            <dd>{{ row.value }}</dd>
          </div>
        </dl>
      </article>

      <section class="sli-show-section">
        <header class="sli-show-section__head">
          <h2>القطع</h2>
          <router-link
            class="sli-show-link"
            :to="editTo"
          >
            تعديل القطع
          </router-link>
        </header>
        <div class="sli-parcel-grid">
          <article
            v-for="(land, index) in parcels"
            :key="land._key || land.id || index"
            class="sli-show-card sli-parcel"
            :class="index % 2 === 0 ? 'is-navy' : 'is-lime'"
          >
            <header class="sli-parcel__head">
              <div>
                <h3>قطعة {{ index + 1 }}</h3>
                <p>{{ parcelTitle(land) }}</p>
              </div>
              <span
                v-if="land.tenure_type"
                class="sli-badge"
              >{{ land.tenure_type }}</span>
            </header>
            <dl class="sli-facts sli-facts--dense">
              <div
                v-for="row in parcelFacts(land)"
                :key="`${index}-${row.label}`"
                class="sli-fact"
              >
                <dt>{{ row.label }}</dt>
                <dd>{{ row.value }}</dd>
              </div>
            </dl>
            <div
              v-if="parcelFiles(land).length"
              class="sli-docs sli-docs--inline"
            >
              <a
                v-for="file in parcelFiles(land)"
                :key="file.key"
                class="sli-doc sli-doc--sm"
                :href="file.url"
                target="_blank"
                rel="noopener"
              >
                {{ file.label }}
              </a>
            </div>
            <div class="sli-enc-list">
              <h4>التعديات ({{ (land.encroacher_data || []).length }})</h4>
              <div
                v-if="!(land.encroacher_data || []).length"
                class="sli-empty"
              >
                لا توجد تعديات على هذه القطعة
              </div>
              <div
                v-for="enc in (land.encroacher_data || [])"
                :key="enc.id || enc._key"
                class="sli-enc"
              >
                <div class="sli-enc__meta">
                  <strong>{{ display(enc.name) }}</strong>
                  <span
                    v-if="enc.service_label || serviceLabel(enc.service_id)"
                    class="sli-badge sli-badge--sm"
                  >{{ enc.service_label || serviceLabel(enc.service_id) }}</span>
                  <p>
                    قومي {{ display(enc.national_id) }}
                    — هاتف {{ display(enc.phone) }}
                    — {{ display(enc.area_m2) }} م²
                  </p>
                </div>
                <div class="sli-enc__actions">
                  <router-link
                    v-if="enc.project_id"
                    class="sli-show-btn sli-show-btn--tiny sli-show-btn--success"
                    :to="{ name: 'project_details', params: { id: enc.project_id } }"
                  >
                    المشروع {{ enc.project_id }}
                  </router-link>
                  <template v-else-if="canCreateProject">
                    <button
                      type="button"
                      class="sli-show-btn sli-show-btn--tiny sli-show-btn--primary"
                      :disabled="!enc.id || creatingKey === encKey(enc, 109)"
                      @click="createEncProject(enc, 109)"
                    >
                      مشروع زراعي
                    </button>
                    <button
                      type="button"
                      class="sli-show-btn sli-show-btn--tiny sli-show-btn--ghost"
                      :disabled="!enc.id || creatingKey === encKey(enc, 110)"
                      @click="createEncProject(enc, 110)"
                    >
                      مشروع أحكار
                    </button>
                  </template>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section class="sli-show-card">
        <header class="sli-show-card__head">
          فريق العمل
        </header>
        <ul
          v-if="teamMembers.length"
          class="sli-team"
        >
          <li
            v-for="(member, i) in teamMembers"
            :key="member._key || i"
          >
            <strong>{{ memberName(member) }}</strong>
            <span v-if="member.role"> — {{ member.role }}</span>
            <span
              v-if="member.is_lead"
              class="sli-badge sli-badge--sm"
            >قائد</span>
          </li>
        </ul>
        <p
          v-else
          class="sli-empty"
        >
          لا يوجد فريق عمل
        </p>
      </section>
    </template>

    <sli-link-seizure-modal
      :visible.sync="linkModal"
      :inventory-id="inventoryId"
      @linked="load"
    />

    <b-modal
      id="sli-media-modal"
      v-model="mediaOpen"
      size="xl"
      centered
      hide-footer
      lazy
      title="المستندات والخريطة"
      modal-class="sli-media-modal"
      body-class="sli-media-modal__body"
      @shown="onMediaShown"
      @hidden="onMediaHidden"
    >
      <div class="sli-media">
        <aside class="sli-media__docs">
          <h6>المستندات</h6>
          <div
            v-if="attachments.length"
            class="sli-docs"
          >
            <a
              v-for="file in attachments"
              :key="file.id || file.url || file.name"
              class="sli-doc"
              :href="file.download_url || file.url"
              target="_blank"
              rel="noopener"
            >
              <span class="sli-doc__kind">{{ isPdf(file) ? 'PDF' : 'ملف' }}</span>
              <span class="sli-doc__name">{{ file.name }}</span>
              <span class="sli-doc__go">فتح</span>
            </a>
          </div>
          <p
            v-else
            class="sli-empty"
          >
            لا توجد مستندات مرفقة
          </p>
        </aside>
        <div class="sli-media__map">
          <h6>الخريطة</h6>
          <sli-polygons-map
            v-if="mediaReady"
            ref="mediaMap"
            :locations="parcels"
            :active="mediaReady"
            title=""
            flush
            fill
          />
          <p
            v-if="!hasMap"
            class="sli-empty"
          >
            لا توجد إحداثيات KML على القطع
          </p>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { BSpinner, BModal } from 'bootstrap-vue'
import seizureLandInventoriesApi from '@/api/seizure-land-inventories/seizureLandInventories'
import SliLinkSeizureModal from './components/SliLinkSeizureModal.vue'
import SliPolygonsMap from './components/SliPolygonsMap.vue'
import {
  inventoryFromResponse,
  inventoryToForm,
  existingAttachments,
  formatFqs,
  formatShahrYear,
  encroachmentsCount,
  linkedParcelsCount,
  canLinkSeizure,
  mapCoordinates,
  locationHasPolygon,
  createProjectBody,
  serviceLabel,
  SERVICE_AGRICULTURAL,
  SERVICE_AHKAR,
  numericInventoryId,
  HEADER_AGRICULTURAL_REFORM,
  HEADER_PRIVATE_STATE_PROPERTY,
  isMushaa,
  isMufraz,
} from './formModel'

export default {
  name: 'SeizureLandInventoryShow',
  components: {
    BSpinner,
    BModal,
    SliLinkSeizureModal,
    SliPolygonsMap,
  },
  data() {
    return {
      loading: false,
      inventory: null,
      form: inventoryToForm({}),
      attachments: [],
      linkModal: false,
      mediaOpen: false,
      mediaReady: false,
      creatingKey: '',
    }
  },
  computed: {
    inventoryId() {
      return numericInventoryId(this.inventory?.id || this.$route.params.id)
    },
    parcels() {
      return this.form.project_locations || []
    },
    teamMembers() {
      return (this.form.team_members || []).filter(row => row.employee_id || this.memberName(row) !== '—')
    },
    hasMap() {
      return this.parcels.some(locationHasPolygon)
    },
    mediaCount() {
      return this.attachments.length + (this.hasMap ? 1 : 0)
    },
    canLink() {
      return canLinkSeizure({
        ...this.form,
        parcels: this.parcels,
      })
    },
    canCreateProject() {
      return this.hasPermission('create projects')
    },
    listQuery() {
      const tab = this.$route.query.tab || this.form.header_type
      return tab ? { tab } : {}
    },
    editTo() {
      return {
        name: 'seizure-land-inventories-edit',
        params: { id: this.inventoryId || this.$route.params.id },
        query: this.listQuery,
      }
    },
    headerTypeLabel() {
      if (this.form.header_type === HEADER_PRIVATE_STATE_PROPERTY) return 'أملاك الدولة الخاصة'
      if (this.form.header_type === HEADER_AGRICULTURAL_REFORM) return 'إصلاح زراعي'
      return this.form.header_type || ''
    },
    headerFacts() {
      const first = this.parcels[0] || {}
      return this.factRows([
        ['اسم الخاضع', this.form.subject_name],
        ['هاتف الخاضع', this.form.subject_phone],
        ['رقم الشهر', this.form.shahr_number],
        ['سنة الشهر', formatShahrYear(this.form)],
        ['المديرية', this.form.directorate],
        ['المنطقة', this.form.zone],
        ['القانون', this.form.applicable_law],
        ['سنة القانون', this.form.law_year],
        ['كود الأرض', this.form.land_code || first.code],
        ['المحافظة', first.governorate || this.form.governorate],
        ['المركز', first.center || this.form.center],
        ['القرية/الناحية', first.village || first.nahia || this.form.village],
        ['جهة الولاية', this.form.jurisdiction_authority],
        ['نوع القرار', this.form.decision_type],
        ['الجمعية التابعة', this.form.affiliated_association],
      ])
    },
  },
  created() {
    this.load()
  },
  methods: {
    encroachmentsCount,
    linkedParcelsCount,
    serviceLabel,
    display(value) {
      const text = value == null ? '' : String(value).trim()
      return text === '' ? '—' : text
    },
    factRows(pairs) {
      return pairs
        .map(([label, value]) => ({ label, value: this.display(value) }))
        .filter(row => row.value !== '—')
    },
    isPdf(file) {
      const name = String(file && file.name || '').toLowerCase()
      const mime = String(file && file.mime || '')
      return mime.includes('pdf') || name.endsWith('.pdf')
    },
    parcelTitle(land) {
      return [
        land.basin_name,
        land.block_number || land.basin_number,
        land.land_number || land.parcel_number || land.plot_number,
      ].filter(v => String(v || '').trim()).join(' — ') || '—'
    },
    parcelFacts(land) {
      const area = formatFqs(land.area_feddan, land.area_qirat, land.area_sahm) || land.area_fqs
      const mushaa = isMushaa(land.tenure_type)
        ? formatFqs(land.mushaa_feddan, land.mushaa_qirat, land.mushaa_sahm) || land.mushaa_total
        : ''
      const coords = mapCoordinates(land.coordinates).length
      return [
        ...this.factRows([
          ['المحافظة', land.governorate],
          ['المنطقة', land.zone],
          ['المركز', land.center],
          ['القرية/الناحية', land.village || land.nahia],
          ['اسم الحوض', land.basin_name],
          ['رقم الحوض', land.block_number || land.basin_number],
          ['رقم القطعة', land.land_number || land.parcel_number || land.plot_number],
          ['المساحة ف/ط/س', area],
          ['من إجمالي كم', mushaa],
          ['رقم القطعة حديث', isMufraz(land.tenure_type) ? land.new_land_number : ''],
          ['صاحب التكليف', land.taxpayer_name || (land.customer && land.customer.name)],
          ['رقم التكليف', land.taxpayer_number],
          ['كود الأرض', land.code],
          ['نوع الاستخدام', land.usage_type],
          ['بحري', land.northern_limit],
          ['شرقي', land.eastern_limit],
          ['قبلي', land.southern_limit],
          ['غربي', land.western_limit],
          ['ملاحظات', land.notes],
        ]),
        ...(coords ? [{ label: 'نقاط KML', value: String(coords) }] : []),
      ]
    },
    parcelFiles(land) {
      return [
        land.kmlExisting ? { key: 'kml', label: 'KML', url: land.kmlExisting.url || land.kmlExisting.download_url } : null,
        land.mapExisting ? { key: 'map', label: 'خريطة', url: land.mapExisting.url || land.mapExisting.download_url } : null,
        land.newMapExisting ? { key: 'new', label: 'زمام جديد', url: land.newMapExisting.url || land.newMapExisting.download_url } : null,
      ].filter(item => item && item.url)
    },
    memberName(member) {
      return this.display(
        (member.employee && (member.employee.full_name || member.employee.name))
        || member.full_name
        || member.employee_id,
      )
    },
    encKey(enc, serviceId) {
      return `${enc.id || enc._key}-${serviceId}`
    },
    openMedia() {
      this.mediaOpen = true
    },
    onMediaShown() {
      this.mediaReady = true
      this.$nextTick(() => {
        this.$nextTick(() => {
          if (this.$refs.mediaMap && this.$refs.mediaMap.renderMap) {
            this.$refs.mediaMap.renderMap()
          }
        })
      })
    },
    onMediaHidden() {
      this.mediaReady = false
    },
    load() {
      this.loading = true
      seizureLandInventoriesApi.show(this.$route.params.id)
        .then(res => {
          const inventory = inventoryFromResponse(res)
          this.inventory = inventory
          this.form = inventoryToForm(inventory)
          this.attachments = existingAttachments(inventory)
        })
        .catch(() => {
          this.$toast.error('تعذر تحميل الحصر')
        })
        .finally(() => {
          this.loading = false
        })
    },
    createEncProject(enc, serviceId) {
      if (Number(serviceId) === 108) return
      if (!this.inventoryId || !enc || !enc.id) {
        this.$toast.error('يلزم حفظ التعدي أولاً للحصول على معرّف')
        return
      }
      if (enc.project_id) return
      const sid = Number(serviceId) === SERVICE_AHKAR ? SERVICE_AHKAR : SERVICE_AGRICULTURAL
      const body = createProjectBody(enc.service_id || sid)
      body.service_id = sid
      this.creatingKey = this.encKey(enc, sid)
      seizureLandInventoriesApi.createEncroachmentProject(this.inventoryId, enc.id, body)
        .catch(err => {
          if (err?.response?.status === 404) {
            return seizureLandInventoriesApi.createProject(this.inventoryId, { ...body, encroachment_id: enc.id })
          }
          return Promise.reject(err)
        })
        .then(res => {
          const payload = inventoryFromResponse(res) || res
          const project = payload?.project || res?.data?.project || res?.project
          const projectId = project?.id || payload?.project_id || res?.data?.project_id
          this.$toast.success(projectId ? `تم إنشاء المشروع ${projectId}` : 'تم إنشاء مشروع التعدي')
          this.load()
          if (projectId) {
            this.$router.push({ name: 'project_details', params: { id: projectId } })
          }
        })
        .catch(err => {
          const status = err?.response?.status
          const data = err?.response?.data
          if (status === 409) {
            const projectId = data?.project_id || data?.data?.project_id || data?.project?.id
            this.$toast.error(projectId ? `هذا التعدي له مشروع مسبقاً (${projectId})` : 'هذا التعدي له مشروع مسبقاً')
            this.load()
            return
          }
          this.$toast.error(data?.message || 'تعذر إنشاء مشروع التعدي')
        })
        .finally(() => {
          this.creatingKey = ''
        })
    },
  },
}
</script>

<style scoped>
.sli-show-page {
  background: #e8eef5;
  margin: -1.2rem -1.2rem 0;
  padding: 12px;
  min-height: calc(100vh - 80px);
  width: auto;
  max-width: none;
}
.sli-show-bar {
  position: sticky;
  top: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #06284D;
  border-top: 8px solid #5CC10B;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 12px;
  box-shadow: 0 8px 24px rgba(6, 40, 77, 0.18);
}
.sli-show-bar h1 {
  margin: 0 0 4px;
  color: #fff;
  font-size: 1.25rem;
  font-weight: 700;
}
.sli-show-bar p {
  margin: 0;
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.92rem;
}
.sli-show-bar__nav {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  pointer-events: auto;
}
.sli-show-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 16px;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.9rem;
  text-decoration: none !important;
  border: 1px solid transparent;
  cursor: pointer;
  pointer-events: auto;
}
.sli-show-btn--primary {
  background: #5CC10B;
  color: #fff;
}
.sli-show-btn--primary:hover {
  color: #fff;
  filter: brightness(1.05);
}
.sli-show-btn--ghost {
  background: transparent;
  color: #fff;
  border-color: rgba(255, 255, 255, 0.35);
}
.sli-show-btn--ghost:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
}
.sli-show-btn--success {
  background: #eef6e4;
  color: #06284D;
  border-color: #5CC10B;
}
.sli-show-btn--tiny {
  min-height: 34px;
  padding: 0 10px;
  font-size: 0.78rem;
}
.sli-show-btn__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  margin-right: 6px;
  padding: 0 5px;
  border-radius: 999px;
  background: #5CC10B;
  color: #fff;
  font-size: 11px;
  line-height: 18px;
}
.sli-show-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.sli-show-loading {
  text-align: center;
  padding: 32px 0;
}
.sli-show-kpis {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}
.sli-kpi {
  background: #fff;
  color: #06284D;
  border: 1px solid rgba(6, 40, 77, 0.12);
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 0.82rem;
  font-weight: 700;
}
.sli-kpi--muted {
  font-weight: 600;
  color: #5f6368;
}
.sli-show-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(6, 40, 77, 0.12);
  overflow: hidden;
  min-width: 0;
}
.sli-show-card--header {
  width: 100%;
  height: auto;
  margin-bottom: 12px;
  align-self: start;
}
.sli-show-card__head,
.sli-show-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  font-weight: 700;
  color: #06284D;
  border-bottom: 1px solid rgba(6, 40, 77, 0.08);
}
.sli-show-section {
  margin-bottom: 12px;
}
.sli-show-section__head {
  background: transparent;
  border: 0;
  padding: 4px 2px 10px;
}
.sli-show-section h2 {
  margin: 0;
  font-size: 1.05rem;
  color: #06284D;
}
.sli-show-link {
  color: #06284D;
  font-weight: 700;
  font-size: 0.85rem;
}
.sli-facts {
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  width: 100%;
}
.sli-facts--dense .sli-fact {
  flex-basis: 120px;
}
.sli-fact {
  flex: 1 1 148px;
  max-width: 100%;
  padding: 8px 14px 10px;
  border-left: 1px solid rgba(6, 40, 77, 0.08);
  border-top: 1px solid rgba(6, 40, 77, 0.08);
  min-width: 0;
}
.sli-fact dt {
  font-size: 11px;
  font-weight: 700;
  color: #5f6368;
  margin-bottom: 2px;
}
.sli-fact dd {
  margin: 0;
  color: #06284D;
  font-weight: 600;
  font-size: 0.95rem;
  word-break: break-word;
}
.sli-docs {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
}
.sli-docs--inline {
  flex-direction: row;
  flex-wrap: wrap;
  padding-top: 0;
}
.sli-doc {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f4f7fb;
  border: 1px solid rgba(6, 40, 77, 0.1);
  border-radius: 8px;
  padding: 8px 10px;
  color: #06284D;
  text-decoration: none !important;
}
.sli-doc__kind {
  background: #06284D;
  color: #fff;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
}
.sli-doc__name {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  font-size: 0.85rem;
}
.sli-doc__go {
  color: #5CC10B;
  font-weight: 700;
  font-size: 0.78rem;
}
.sli-doc--sm {
  padding: 6px 10px;
  font-size: 0.78rem;
  font-weight: 700;
}
.sli-empty {
  margin: 0;
  padding: 12px 14px;
  color: #5f6368;
  font-size: 0.88rem;
}
.sli-parcel-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 360px), 1fr));
  gap: 12px;
}
.sli-parcel.is-navy {
  border-right: 5px solid #06284D;
}
.sli-parcel.is-lime {
  border-right: 5px solid #5CC10B;
}
.sli-parcel__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px 4px;
}
.sli-parcel h3 {
  margin: 0;
  font-size: 1rem;
  color: #06284D;
}
.sli-parcel p {
  margin: 2px 0 0;
  color: #5f6368;
  font-size: 0.82rem;
}
.sli-badge {
  display: inline-flex;
  align-items: center;
  background: #eef6e4;
  color: #06284D;
  border: 1px solid #5CC10B;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}
.sli-badge--sm {
  padding: 1px 8px;
  font-size: 0.7rem;
}
.sli-enc-list {
  padding: 8px 14px 14px;
  border-top: 1px solid rgba(6, 40, 77, 0.08);
}
.sli-enc-list h4 {
  margin: 0 0 8px;
  font-size: 0.85rem;
  color: #06284D;
}
.sli-enc {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 0;
  border-top: 1px solid #eee;
}
.sli-enc__meta p {
  margin: 4px 0 0;
  color: #5f6368;
  font-size: 0.8rem;
}
.sli-enc__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: flex-start;
  flex: 0 0 auto;
}
.sli-team {
  list-style: none;
  margin: 0;
  padding: 8px 14px 14px;
}
.sli-team li {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 8px 0;
  border-top: 1px solid #f0f0f0;
  color: #06284D;
}
.sli-team li:first-child {
  border-top: 0;
}
.sli-media {
  display: grid;
  grid-template-columns: minmax(220px, 280px) minmax(0, 1fr);
  min-height: 420px;
}
.sli-media__docs {
  background: #f4f7fb;
  border-left: 1px solid rgba(6, 40, 77, 0.08);
  min-width: 0;
}
.sli-media h6 {
  margin: 0;
  padding: 12px 14px 4px;
  font-size: 0.82rem;
  font-weight: 700;
  color: #06284D;
}
.sli-media__map {
  min-width: 0;
  background: #fff;
}
@media (max-width: 991.98px) {
  .sli-show-bar {
    flex-direction: column;
    align-items: stretch;
    position: static;
  }
  .sli-show-bar__nav .sli-show-btn {
    flex: 1 1 auto;
  }
  .sli-media {
    grid-template-columns: 1fr;
  }
  .sli-media__docs {
    border-left: 0;
    border-bottom: 1px solid rgba(6, 40, 77, 0.08);
  }
}
@media (max-width: 575.98px) {
  .sli-show-page {
    margin: -1rem -1rem 0;
    padding: 10px;
  }
  .sli-enc {
    flex-direction: column;
  }
  .sli-facts,
  .sli-facts--dense {
    display: flex;
  }
  .sli-fact {
    flex-basis: calc(50% - 1px);
  }
}
</style>

<style>
.sli-media-modal .modal-dialog {
  max-width: min(1120px, calc(100vw - 24px));
  margin: 12px auto;
}
.sli-media-modal .modal-content {
  border: 0;
  border-radius: 10px;
  overflow: hidden;
}
.sli-media-modal .modal-header {
  background: #06284D;
  color: #fff;
  border-bottom: 4px solid #5CC10B;
  align-items: center;
}
.sli-media-modal .modal-title {
  color: #fff;
  font-weight: 700;
}
.sli-media-modal .close {
  color: #06284D;
  text-shadow: none;
  opacity: 0.85;
}
.sli-media-modal .close:hover {
  color: #06284D;
  opacity: 1;
}
.sli-media-modal .sli-media-modal__body {
  padding: 0;
}
@media (max-width: 575.98px) {
  .sli-media-modal .modal-dialog {
    margin: 8px;
    max-width: calc(100vw - 16px);
  }
}
</style>
